import { DOCUMENT }      from '@angular/common';
import {
	inject,
	Injectable
}                        from '@angular/core';
import { Router }        from '@angular/router';
import { IMedia }        from '@app/core/interfaces/media-interfaces';
import { LoggerService } from '@app/shared/services/logger.service';
import { MediaService }  from '@core/services/media/media.service';
import {
	UiStoreActions,
	UiStoreSelectors
}                        from '@core/store/feature/ui';
import { IPageLoader }   from '@core/store/feature/ui/state';
import { LocaleEnum }    from '@models/language';
import { Store }         from '@ngrx/store';
import {
	TranslateService,
	TranslationChangeEvent
}                        from '@ngx-translate/core';
import { Observable }    from 'rxjs';

@Injectable( { providedIn: 'root' } )
export class UiService {

	private readonly _document: Document = inject( DOCUMENT );
	private _language: string;
	private _mediaData: IMedia;
	private readonly _mediaData$: Observable<IMedia>;
	private _pageLoader: IPageLoader;
	private readonly _pageLoader$: Observable<IPageLoader>;
	private readonly _pageLoaderVisibility$: Observable<boolean>;

	public languageHasChanged$: Observable<string>;

	constructor(
		private readonly _logger: LoggerService,
		private readonly _mediaService: MediaService,
		private readonly _router: Router,
		private readonly _translateService: TranslateService,
		protected readonly store$: Store
	) {
		this._mediaData$ = this.store$.select( UiStoreSelectors.selectMediaData );
		this._mediaData$.subscribe( ( mediaData: IMedia ) => {
			this._mediaData = { ...mediaData };
		} );

		this._pageLoader$ = this.store$.select( UiStoreSelectors.selectPageLoader );
		this._pageLoader$.subscribe( ( loader: IPageLoader ) => {
			this._pageLoader = loader;
		} );

		this._pageLoaderVisibility$ = this.store$.select( UiStoreSelectors.selectPageLoaderVisibility );
		this._pageLoaderVisibility$.subscribe( ( showLoader: boolean ) => {
			this._pageLoader = { ...this._pageLoader, show: showLoader };
		} );

		this.store$.select( UiStoreSelectors.selectLanguage )
		.subscribe( ( lang: string ) => {
			this._language = lang;
		} );

		this._translateService.onLangChange
		.subscribe( ( evt: TranslationChangeEvent ) => {
			this.changeLanguage( evt.lang );
		} );

		this._translateService.onDefaultLangChange
		.subscribe( ( evt: TranslationChangeEvent ) => {
			this.changeLanguage( evt.lang );
		} );
	}

	// PageLoader
	private hidePageLoaderAndResetCounter = (): void => {
		if ( this._pageLoader.show ) {
			this.store$.dispatch( new UiStoreActions.HidePageLoaderAndResetCounter( {
				show: false,
				counter: 0
			} ) );
		}
	};

	showPageLoaderAndCountUp(): void {
		let { show, counter } = this._pageLoader;

		if ( !show && !counter ) {
			show = true;
			counter = 1;
		} else {
			counter += 1;
		}

		this.store$.dispatch( new UiStoreActions.ShowPageLoaderAndCountUp( { show, counter } ) );
	}

	hidePageLoaderOrCountDown(): void {
		let { show, counter } = this._pageLoader;

		if ( counter > 1 ) {
			counter -= 1;
		} else {
			show = false;
			counter = 0;
		}

		this.store$.dispatch( new UiStoreActions.HidePageLoaderOrCountDown( { show, counter } ) );
	}

	getPageLoaderVisibility(): boolean {
		return this._pageLoader.show;
	}

	getPageLoaderVisibility$(): Observable<boolean> {
		return this._pageLoaderVisibility$;
	}

	// Media
	getMediaData(): IMedia {
		return this._mediaData;
	}

	getMediaData$(): Observable<IMedia> {
		return this._mediaData$;
	}

	// Language
	private changeLanguage( language: string ): void {
		switch ( language ) {
			case 'de':
				this.store$.dispatch( new UiStoreActions.SetUiLanguage( LocaleEnum.GERMAN ) );
				break;

			case 'en':
				this.store$.dispatch( new UiStoreActions.SetUiLanguage( LocaleEnum.ENGLISH ) );
				break;
		}

		this.languageHasChanged$ = this.store$.select( UiStoreSelectors.selectLanguage );
		this._document.documentElement.lang = language;
	}

	getLanguage( asCode: boolean = false ) {
		return asCode ? this._language.split( '-' )[ 0 ] : this._language;
	}

	// Components-Specific Getters
	get navbar() {
		return {
			hidePageLoaderAndResetCounter: this.hidePageLoaderAndResetCounter
		};
	}
}
